import { Qual } from 'types';

export interface OptionalFieldsInput {
  uuid?: string;
  gclid?: string;
  optional1?: string;
  optional2?: string;
  optional3?: string;
  optional4?: string;
  optional5?: string;
}

const SLOTS = [
  ['option1', 'optional1'],
  ['option2', 'optional2'],
  ['option3', 'optional3'],
  ['option4', 'optional4'],
  ['option5', 'optional5'],
] as const;

/**
 * The QUAL is partner-supplied and can be hand-built, bypassing the SDK's own
 * string check. GraphQL's String type rejects an Int outright rather than coercing
 * it, so a numeric partner id here would fail the entire mutation instead of just
 * degrading a reporting slot. Coerce finite numbers and drop anything non-scalar,
 * which would otherwise serialize as "[object Object]".
 */
export const toSlotValue = (value: unknown): string | undefined => {
  if (typeof value === 'string') return value || undefined;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return undefined;
};

/**
 * Builds the createLead/availableOffers `optionalFields` input from the QUAL.
 *
 * Partner options pass straight through to the matching optional field; Falcon maps
 * `optionalN` onto its Optional1..5 reporting slots. Empty slots are omitted rather
 * than sent blank, because Falcon falls back to a legacy source per slot and an empty
 * string would suppress nothing while still occupying the payload.
 *
 * `gclid` looks redundant next to `optional5` but is not. Serviceability only reads
 * `optionalN` in its createLead path (`generateOptionalSlots`); `getOfferAvailability`
 * still hardcodes `Optional5` from `gclid || msclkid || fbclid` and ignores `optionalN`
 * entirely. Since this builder feeds both operations, gclid is the only route to
 * Optional5 on the availableOffers request. It is inert on createLead, where
 * `optional5` takes precedence. Remove it once getOfferAvailability adopts
 * generateOptionalSlots.
 */
export function buildOptionalFields(qual: Qual): OptionalFieldsInput {
  const { optionalFields, uuid } = qual || {};
  const result: OptionalFieldsInput = {};

  if (uuid) result.uuid = uuid;

  const gclid = toSlotValue(optionalFields?.option5);
  if (gclid) result.gclid = gclid;

  SLOTS.forEach(([from, to]) => {
    const value = toSlotValue(optionalFields?.[from]);
    if (value) result[to] = value;
  });

  return result;
}
